import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";

const links = [
  { label: "Services", href: "#services" },
  { label: "Heritage", href: "#heritage" },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#reviews" },
  { label: "Quote", href: "#quote" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar({ scrollTo, onQuote }) {
  const [open, setOpen] = useState(false);

  const go = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  return (
    <header
      data-testid="site-nav"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-white/85 border-b border-zinc-200/80"
    >
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-brand-logo"
          onClick={() => go("#home")}
          className="flex items-center"
          aria-label="Xtra Clean Carpet Care and More — home"
        >
          <img
            src="/logo.png"
            alt="Xtra Clean Carpet Care and More logo"
            className="h-11 sm:h-12 w-auto"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="font-mono-x text-xs uppercase tracking-[0.2em] text-zinc-600 hover:text-red-600 transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            data-testid="nav-phone-button"
            href="tel:+13252345947"
            className="flex items-center gap-2 border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-all duration-200 px-4 py-2 font-mono-x text-xs font-semibold tracking-wider active:scale-[0.98]"
          >
            <Phone size={14} /> 325-234-5947
          </a>
          <button
            data-testid="nav-quote-button"
            onClick={onQuote}
            className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 font-mono-x text-xs font-semibold uppercase tracking-wider transition-colors duration-200 shadow-lg shadow-red-600/20 active:scale-[0.98]"
          >
            Free Quote
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-zinc-900"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-drawer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-md border-t border-zinc-200"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="text-left font-display font-bold text-2xl tracking-tight text-zinc-900 hover:text-red-600 transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <a
                data-testid="nav-mobile-phone-button"
                href="tel:+13252345947"
                className="mt-2 flex items-center justify-center gap-2 bg-red-600 text-white py-3 font-mono-x text-sm font-semibold tracking-wider"
              >
                <Phone size={16} /> 325-234-5947
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
